import type { ReactNode } from 'react';
import { Chip } from '@nexvia-logistics/ui';
import { LockersIcon } from '@/shared/icons';

/**
 * The split sign-in shell: blue panel with the headline and photo cards on one side, the form
 * column on the other; stacked (panel on top) below `lg`.
 *
 * Storage feedback (2026-10-26): a copy of nexvia-web-app's
 * `src/features/auth/components/AuthSplitLayout.tsx` (itself a copy of nexvia-landing's carrier
 * sign-up), so the depósito login looks like the client one. Both apps compile the same
 * `@nexvia-logistics/tokens` theme, so the same classes render the same pixels — keep them in step
 * with the web's when either changes. What differs is the copy: the headline speaks to depósitos,
 * and the form column opens with the sidebar's "Panel del depósito" chip so the screen says whose
 * panel it is before the title does. The photos are the web's own files, copied into
 * `public/login-assets/` (decorative: empty `alt`).
 *
 * The outer `fixed inset-0` is the web's — it lifts the screen out of any layout and, from `lg`
 * up, centres the block both ways.
 */

const PHOTO_FRAME =
  'absolute aspect-[260/208] w-[50.6%] overflow-hidden rounded-[8.7px] border-[4.456px] border-outline-muted shadow-[0_0_11px_rgba(0,0,0,0.25)] md:aspect-auto md:h-[208px] md:w-[260px] md:rounded-card md:border-[6.375px] md:shadow-[0_0_16px_rgba(0,0,0,0.25)] lg:h-[33.6%] lg:w-[47%] lg:rounded-card lg:border-[8.26px] lg:shadow-[0_0_21px_rgba(0,0,0,0.25)]';

// Back to front: the left card sits under the right one, the centre card over both.
const PHOTOS = [
  {
    src: '/login-assets/login-photo-1.webp',
    position: 'z-10 left-[-23.5%] top-[157px] rotate-[-7.09deg] md:left-[-23px] md:top-[178px] lg:left-[-14.9%] lg:top-[41.5%]'
  },
  {
    src: '/login-assets/login-photo-3.webp',
    position: 'z-20 left-[68.2%] top-[141px] rotate-[6.79deg] md:left-[446px] md:top-[155px] lg:left-[69.8%] lg:top-[37.9%]'
  },
  {
    src: '/login-assets/login-photo-2.webp',
    position: 'z-30 left-[25.7%] top-[189px] rotate-[3.35deg] md:left-[229px] md:top-[223px] lg:left-[30.7%] lg:top-[48.9%]'
  }
];

interface AuthSplitLayoutProps {
  /** The page's only `h1`. */
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
}

export default function AuthSplitLayout({ title, description, children }: AuthSplitLayoutProps) {
  return (
    <div className="fixed inset-0 overflow-auto bg-surface lg:flex lg:items-center lg:justify-center">
      <div className="flex min-h-[100dvh] flex-col bg-surface pb-10 lg:mx-auto lg:h-[100dvh] lg:max-h-[880px] lg:min-h-0 lg:w-full lg:max-w-[1440px] lg:flex-row lg:items-stretch lg:gap-8 lg:overflow-hidden lg:p-8">
        <div className="relative mx-4 mt-6 h-[340px] shrink-0 overflow-hidden rounded-panel bg-primary md:mx-auto md:mt-8 md:h-[500px] md:w-[calc(100%-48px)] md:max-w-[708px] lg:m-0 lg:h-full lg:w-auto lg:max-w-none lg:flex-none lg:basis-[52%]">
          <div className="relative size-full">
            <p className="absolute left-1/2 top-10 z-10 m-0 w-[249px] -translate-x-1/2 text-center text-display-medium font-semibold text-on-primary md:top-14 md:w-[278px] md:text-title-xlarge md:leading-none md:tracking-[-0.09375rem] lg:top-[13.75%] lg:w-[68.7%] lg:text-title-xlarge xl:text-display-medium">
              Tu depósito, conectado a Nexvia.
            </p>

            {PHOTOS.map((photo) => (
              <div key={photo.src} className={`${PHOTO_FRAME} ${photo.position}`}>
                <img src={photo.src} alt="" className="block size-full object-cover" loading="eager" />
              </div>
            ))}
          </div>
        </div>

        <main className="flex flex-1 flex-col items-center justify-start px-6 pt-8 md:justify-center md:px-[120px] md:py-10 lg:h-full lg:min-h-0 lg:justify-center lg:overflow-y-auto lg:px-20 lg:py-10">
          <div className="flex w-full max-w-[380px] flex-col items-center">
            <img src="/favicon.svg" alt="Nexvia" className="mb-5 block size-10 object-contain" width={40} height={40} />

            <Chip tone="info" icon={<LockersIcon size={14} />} className="mb-4">
              Panel del depósito
            </Chip>

            <h1 className="m-0 mb-2 w-full text-center text-title-medium font-semibold leading-[1.167] tracking-[-0.046875rem] text-on-surface lg:text-title-large">
              {title}
            </h1>
            {description ? (
              <p className="m-0 mb-5 w-full text-center text-body-large font-normal leading-[1.444] text-on-surface-tertiary lg:mb-8">
                {description}
              </p>
            ) : null}

            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
